import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { createOffer, listOffers, patchOffer, type CreateOfferInput } from '../api/endpoints/offers';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import { DAY_PLURAL, daysSummary } from '../lib/offer-days';
import { offerDateInput, offerInput } from '../lib/offer-form';
import type { BusinessOffer } from '../api/schemas';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { FormField } from '../components/FormField';
import { LoadingState } from '../components/LoadingState';
import { OfferDayPicker } from '../components/OfferDayPicker';
import { PageHeader } from '../components/PageHeader';
import { formatDateTime, formatMoneyCents } from '../lib/format';

const OFFERS_QUERY_KEY = ['business-offers'];
const DEFAULT_PERCENT_OFF = 10;
type DiscountType = 'percent' | 'fixed';

/** `initialDays`: Inteligencia's link (`?dias=2`) opens the form already set for its weak day. */
export function OfertasPage({ initialDays = [] }: { initialDays?: number[] } = {}) {
  const queryClient = useQueryClient();
  const offersQuery = useQuery({ queryKey: OFFERS_QUERY_KEY, queryFn: listOffers });
  const suggested = initialDays.length > 0;
  const formRef = useRef<HTMLElement>(null);

  const [title, setTitle] = useState(() => suggestedTitle(initialDays));
  const [description, setDescription] = useState(() => suggestedDescription(initialDays));
  const [discountType, setDiscountType] = useState<DiscountType>('percent');
  const [percentOff, setPercentOff] = useState(suggested ? SUGGESTED_PERCENT_OFF : DEFAULT_PERCENT_OFF);
  const [amountInput, setAmountInput] = useState('');
  const [amountError, setAmountError] = useState<string | undefined>();
  const [startsAt, setStartsAt] = useState(() => (suggested ? offerDateInput(nextHourISO()) : ''));
  const [validUntil, setValidUntil] = useState(() => (suggested ? offerDateInput(inDaysISO(SUGGESTED_RUN_DAYS)) : ''));
  const [validDays, setValidDays] = useState<number[]>(initialDays);
  const [editing, setEditing] = useState<BusinessOffer | null>(null);

  // A suggested oferta is the reason the owner came: bring its form into view.
  useEffect(() => {
    if (suggested) formRef.current?.scrollIntoView?.({ block: 'start' });
  }, [suggested]);

  const createMutation = useMutationAttempt({
    mutationFn: ({ id, body, clearsDays }: { id: string | null; body: CreateOfferInput; clearsDays: boolean }, key: string) =>
      id ? patchOffer(id, clearsDays ? { ...body, validDays: null } : body, key) : createOffer(body, key),
    exito: (variables) => ((variables as { id: string | null }).id ? 'Cambios guardados' : 'Oferta creada'),
    onSuccess: () => {
      setEditing(null);
      setDiscountType('percent');
      setTitle('');
      setDescription('');
      setPercentOff(DEFAULT_PERCENT_OFF);
      setAmountInput('');
      setAmountError(undefined);
      setStartsAt('');
      setValidUntil('');
      setValidDays([]);
      void queryClient.invalidateQueries({ queryKey: OFFERS_QUERY_KEY });
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (createMutation.uncertain) { createMutation.retry(); return; }
    const result = offerInput({ title, description, discountType, percentOff, amountInput, startsAt, validUntil, validDays }, editing);
    if (!result.ok) { setAmountError(result.message); return; }
    setAmountError(undefined);
    createMutation.mutate({ id: editing?.id ?? null, body: result.input, clearsDays: result.clearsDays });
  }

  function editOffer(offer: BusinessOffer | null) {
    if (createMutation.isPending || createMutation.uncertain) return;
    setEditing(offer);
    setTitle(offer?.title ?? '');
    setDescription(offer?.description ?? '');
    setDiscountType(offer?.discount.type === 'fixed' ? 'fixed' : 'percent');
    setPercentOff(offer?.discount.type === 'percent' ? offer.discount.percent : DEFAULT_PERCENT_OFF);
    setAmountInput(offer?.discount.type === 'fixed' ? (offer.discount.amountCents / 100).toFixed(2) : '');
    setStartsAt(offer ? offerDateInput(offer.startsAt) : '');
    setValidUntil(offer ? offerDateInput(offer.validUntil) : '');
    setValidDays(offer?.validDays ?? []);
    setAmountError(undefined);
    createMutation.reset();
  }

  return (
    <main>
      <PageHeader title="Ofertas" description="Las promociones activas e inactivas de tu negocio." />
      {offersQuery.isLoading ? <LoadingState label="Cargando ofertas…" /> : null}
      {offersQuery.isError ? <ErrorBanner message={errorMessage(offersQuery.error)} /> : null}
      {offersQuery.data ? (
        offersQuery.data.data.length === 0 ? (
          <EmptyState
            title="Todavía no tienes ofertas."
            description="Una promoción activa aparece en la app de tus clientes al reservar."
          />
        ) : (
          <ul className="rows">
            {offersQuery.data.data.map((offer) => (
              <li key={offer.id}>
                <OfferRow offer={offer} onEdit={() => editOffer(offer)} disabled={createMutation.isPending || createMutation.uncertain} />
              </li>
            ))}
          </ul>
        )
      ) : null}

      <section className="section" ref={formRef} aria-label={editing ? 'Editar oferta' : 'Nueva oferta'}>
        <h2 className="section-label">{editing ? 'Editar oferta' : 'Nueva oferta'}</h2>
        <form className="card form-grid cols-2" onSubmit={handleSubmit} noValidate>
          {suggested && !editing ? (
            <p className="notice span-2">
              Dale Agents sugiere esta oferta: los {suggestedDaysLabel(initialDays)} casi no tienen reservas. Revisa el descuento y las fechas, y créala.
            </p>
          ) : null}
          <FormField label="Título" htmlFor="offer-title" className="span-2">
            <input
              id="offer-title"
              placeholder="Martes de ahorro"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Descripción" htmlFor="offer-description" className="span-2">
            <textarea
              id="offer-description"
              placeholder="Lo que ve tu cliente al reservar."
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              required
            />
          </FormField>
          <FormField
            label="Descuento"
            htmlFor={discountType === 'percent' ? 'offer-percent' : 'offer-amount'}
            className="span-2"
            error={undefined}
            hint={
              discountType === 'percent'
                ? 'Porcentaje sobre el precio, por ejemplo 10'
                : 'En dólares, por ejemplo 5.00'
            }
          >
            <div className="offer-discount-row">
              <span className="seg mini" role="radiogroup" aria-label="Tipo de descuento">
                <button
                  type="button"
                  aria-pressed={discountType === 'percent'}
                  onClick={() => setDiscountType('percent')}
                >
                  %
                </button>
                <button
                  type="button"
                  aria-pressed={discountType === 'fixed'}
                  onClick={() => setDiscountType('fixed')}
                >
                  $
                </button>
              </span>
              {discountType === 'percent' ? (
                <input
                  id="offer-percent"
                  type="number"
                  min={1}
                  max={100}
                  value={percentOff}
                  onChange={(event) => setPercentOff(Number(event.target.value))}
                  required
                />
              ) : (
                <input
                  id="offer-amount"
                  inputMode="decimal"
                  placeholder="5.00"
                  value={amountInput}
                  onChange={(event) => setAmountInput(event.target.value)}
                  required
                />
              )}
            </div>
          </FormField>
          <OfferDayPicker days={validDays} onChange={setValidDays} />
          <FormField label="Empieza" htmlFor="offer-starts">
            <input
              id="offer-starts"
              type="datetime-local"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
              required
            />
          </FormField>
          <FormField label="Vence" htmlFor="offer-valid-until">
            <input
              id="offer-valid-until"
              type="datetime-local"
              value={validUntil}
              onChange={(event) => setValidUntil(event.target.value)}
              required
            />
          </FormField>
          {amountError ? <ErrorBanner message={amountError} /> : null}
          {createMutation.uncertain ? <p className="row-sub span-2">El resultado está pendiente. Reintenta el guardado original antes de hacer otro cambio.</p> : null}
          {createMutation.isError ? <ErrorBanner message={errorMessage(createMutation.error)} /> : null}
          <button type="submit" className="btn" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Guardando…' : createMutation.uncertain ? 'Reintentar guardado' : editing ? 'Guardar cambios' : 'Crear oferta'}
          </button>
          {editing ? <button type="button" className="btn secondary" disabled={createMutation.isPending || createMutation.uncertain} onClick={() => editOffer(null)}>Cancelar edición</button> : null}
        </form>
      </section>
    </main>
  );
}

type OfferRowProps = { offer: BusinessOffer; onEdit(): void; disabled: boolean };

function OfferRow({ offer, onEdit, disabled }: OfferRowProps) {
  const queryClient = useQueryClient();
  const toggleMutation = useMutationAttempt({
    mutationFn: (isActive: boolean, key: string) => patchOffer(offer.id, { isActive }, key),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: OFFERS_QUERY_KEY }),
  });

  return (
    <div className="row">
      <div className="grow">
        <p className="row-title">
          {offer.title} · {discountLabel(offer.discount)}{' '}
          <span className="status" data-tone={offerStatus(offer).tone}>
            {offerStatus(offer).label}
          </span>
        </p>
        <p className="row-sub">
          {[offer.description, daysSummary(offer.validDays), `vence ${formatDateTime(offer.validUntil)}`].filter(Boolean).join(' · ')}
        </p>
        {toggleMutation.isError ? <ErrorBanner message={errorMessage(toggleMutation.error)} /> : null}
      </div>
      <button type="button" className="btn small secondary" aria-label={`Editar oferta ${offer.title}`} disabled={disabled || toggleMutation.isPending || toggleMutation.uncertain} onClick={onEdit}>Editar</button>
      <button
        type="button"
        className="btn small secondary"
        onClick={() => toggleMutation.uncertain ? toggleMutation.retry() : toggleMutation.mutate(!offer.isActive)}
        disabled={disabled || toggleMutation.isPending}
      >
        {toggleMutation.uncertain ? 'Reintentar cambio' : offer.isActive ? 'Desactivar' : 'Activar'}
      </button>
    </div>
  );
}

/** An offer past its window reads Vencida, whatever its flag: the flag says
    intent, the window says whether the POS may still apply it. */
function offerStatus(offer: BusinessOffer): { label: string; tone: 'good' | 'off' | 'hold' } {
  if (!offer.isActive) return { label: 'Inactiva', tone: 'off' };
  if (new Date(offer.validUntil) < new Date()) return { label: 'Vencida', tone: 'hold' };
  return { label: 'Activa', tone: 'good' };
}

/** A suggested oferta runs four weeks: two to fill the weak day, two to see if it held. */
const SUGGESTED_RUN_DAYS = 28;
const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/** Inteligencia's dead-hours advice names 15%, so the suggested form starts there. */
const SUGGESTED_PERCENT_OFF = 15;

function suggestedDescription(days: readonly number[]): string {
  return days.length > 0 ? `${SUGGESTED_PERCENT_OFF}% de descuento los ${suggestedDaysLabel(days)}.` : '';
}

function suggestedTitle(days: readonly number[]): string {
  return days.length > 0 ? `Oferta de los ${suggestedDaysLabel(days)}` : '';
}

function suggestedDaysLabel(days: readonly number[]): string {
  return days.map((day) => DAY_PLURAL[day] ?? '').filter(Boolean).join(' y ');
}

function nextHourISO(): string {
  return new Date(Math.ceil(Date.now() / HOUR_MS) * HOUR_MS).toISOString();
}

function inDaysISO(days: number): string {
  return new Date(Math.ceil(Date.now() / HOUR_MS) * HOUR_MS + days * DAY_MS).toISOString();
}

function discountLabel(discount: BusinessOffer['discount']): string {
  return discount.type === 'percent' ? `${discount.percent}%` : formatMoneyCents(discount.amountCents);
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}
